/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution
} from '@ngt/opms';

import { Chip, styled } from '@mui/material';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';

import { GridColDef } from '@mui/x-data-grid';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { ITreatment } from '@ngt/opms';
import { Patient, PatientStateType } from '../api/dtos';
import { renderSaConsentColumn } from './useRegistrationColumns';
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const useAllocatedColumns = (
    institutions: IInstitution[] | null, 
    showInitials: boolean, 
    showSite: boolean, 
    treatments: ITreatment[] | undefined,
    onSaConsentClick: (patient: Patient) => void,
    isBct: boolean
): GridColDef<Patient>[] => {
    const columns = React.useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Study Number',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials'
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
                //width: 200
            },
            {
                field: 'saConsent',
                headerName: 'SA Consent',
                renderCell: (rowData) => renderSaConsentColumn(rowData, onSaConsentClick, isBct),
                width: 150
            },
            {
                field: 'patientStateId',
                headerName: 'Status',
                valueFormatter: (_value, row) => (
                    row.patientStateId === PatientStateType.BctReview ? 'BCT Review' :
                        row.patientStateId === PatientStateType.CorrectionRequired ? 'Correction Required' :
                            row.patientStateId === PatientStateType.Allocated ? 'Allocated' : 
                                row.patientStateId === PatientStateType.PostAllocated ? 'Post-allocated' : 
                                    row.patientStateId === PatientStateType.ThreeMonthsPostSurgery ? '3 Months Post Surgery' : 
                                        row.patientStateId === PatientStateType.SixMonthsPostSurgery ? '6 Months Post Surgery' : 
                                            row.patientStateId === PatientStateType.TwelveMonthsPostSurgery ? '12 Months Post Surgery' : 
                                                row.patientStateId === PatientStateType.TwentyFourMonthsPostSurgery ? '24 Months Post Surgery' : 
                                                    row.patientStateId === PatientStateType.FiveYearsPostSurgery ? '5 Years Post Surgery' : ''
                        
                ),
                width: 200
            },
            {
                field: 'treatmentId',
                headerName: 'Treatment Arm',
                valueGetter: (_value, row) => (
                    row.treatmentId ? treatments?.find(x => x.id === row.treatmentId)?.name : null
                ),
                width: 150
            },
            {
                field: 'treatmentAllocationDate',
                headerName: 'Date Allocated',
                renderCell: rowData => (
                    rowData.row.treatmentAllocationDate ? DateTime.fromISO(rowData.row.treatmentAllocationDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            },
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [showSite, institutions, treatments, showInitials]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useAllocatedColumns;